/**
 * useAdminProcurement — Admin-Web hooks for Issue #366 (administrative
 * procurement ledger + item movements + dashboard summary + manual finance
 * payment-application trigger).
 *
 * 红线 (AI GUIDE §6 / §7):
 *   - 仅使用 generated @jinzhuan/api-client；不手写 fetch、不构造假状态。
 *   - 金额 / 数量字段 amountDecimal / quantityDecimal 全程以 decimal-safe
 *     string 流转；前端不允许 Number() 化。
 *   - 错误信息走统一文案，409 提示乐观锁冲突，403 提示无权限。
 *   - “生成付款申请” 走 backend command；finance #374 未上线时返回
 *     paymentStatus="not_available"，UI 展示受控空态。
 */
import { useCallback, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type AdminProcurementDto = ApiComponents['schemas']['AdminProcurementDto'];
export type AdminProcurementListDto =
  ApiComponents['schemas']['AdminProcurementListDto'];
export type AdminProcurementEventDto =
  ApiComponents['schemas']['AdminProcurementEventDto'];
export type AdminItemMovementDto =
  ApiComponents['schemas']['AdminItemMovementDto'];
export type AdminItemMovementListDto =
  ApiComponents['schemas']['AdminItemMovementListDto'];
export type AdminProcurementDashboardDto =
  ApiComponents['schemas']['AdminProcurementDashboardDto'];
export type CreateAdminProcurementDto =
  ApiComponents['schemas']['CreateAdminProcurementDto'];
export type UpdateAdminProcurementDto =
  ApiComponents['schemas']['UpdateAdminProcurementDto'];
export type ProcurementLifecycleDto =
  ApiComponents['schemas']['ProcurementLifecycleDto'];
export type RequestPaymentApplicationDto =
  ApiComponents['schemas']['RequestPaymentApplicationDto'];
export type CreateAdminItemMovementDto =
  ApiComponents['schemas']['CreateAdminItemMovementDto'];
export type VoidAdminItemMovementDto =
  ApiComponents['schemas']['VoidAdminItemMovementDto'];

export const ADMIN_PROCUREMENT_STATUS_LABELS: Readonly<
  Record<AdminProcurementDto['status'], string>
> = {
  draft: '草稿',
  confirmed: '已确认',
  payment_requested: '已发起付款申请',
  paid: '已付款',
  voided: '作废',
  archived: '已归档',
};

export const ADMIN_PROCUREMENT_PAYMENT_STATUS_LABELS: Readonly<
  Record<AdminProcurementDto['paymentStatus'], string>
> = {
  not_requested: '未发起',
  pending: '财务待处理',
  paid: '已付款',
  voided: '已作废',
  not_available: '财务服务待上线',
};

export const ADMIN_PROCUREMENT_CATEGORY_LABELS: Readonly<
  Record<AdminProcurementDto['category'], string>
> = {
  office_supplies: '办公耗材',
  it_equipment: 'IT 设备',
  furniture: '家具',
  maintenance: '维护',
  utilities: '水电费',
  travel: '差旅',
  consumable: '日常消耗',
  service: '服务采购',
  gift: '礼品',
  meal_entertainment: '餐饮招待',
  exhibition: '会展',
  business_resource: '业务拓展资源',
  other: '其他',
};

export const ADMIN_ITEM_MOVEMENT_TYPE_LABELS: Readonly<
  Record<AdminItemMovementDto['movementType'], string>
> = {
  in: '入库',
  out: '出库',
  adjust: '调整',
};

export interface AdminProcurementApiError {
  readonly message: string;
  readonly status?: number;
}

function toApiError(err: unknown, status?: number): AdminProcurementApiError {
  if (status === 409) {
    return { message: '记录已被其他操作修改或编码冲突，请刷新后重试', status };
  }
  if (status === 403) return { message: '没有权限执行此操作', status };
  if (status === 400) {
    return { message: '请求参数不合法，请检查必填项与字段格式', status };
  }
  if (status === 404) return { message: '记录不存在或不可访问', status };
  if (err instanceof Error) return { message: err.message, status };
  return { message: String(err), status };
}

export interface ListAdminProcurementFilter {
  search?: string;
  status?: AdminProcurementDto['status'];
  category?: AdminProcurementDto['category'];
  paymentStatus?: AdminProcurementDto['paymentStatus'];
  purchaseDateFrom?: string;
  purchaseDateTo?: string;
  includeArchived?: boolean;
  page?: number;
  limit?: number;
}

export function useAdminProcurements() {
  const [items, setItems] = useState<AdminProcurementDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminProcurementApiError | null>(null);

  const fetchProcurements = useCallback(
    async (filter?: ListAdminProcurementFilter) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, unknown> = {};
        if (filter?.search) query.search = filter.search;
        if (filter?.status) query.status = filter.status;
        if (filter?.category) query.category = filter.category;
        if (filter?.paymentStatus) query.paymentStatus = filter.paymentStatus;
        if (filter?.purchaseDateFrom)
          query.purchaseDateFrom = filter.purchaseDateFrom;
        if (filter?.purchaseDateTo) query.purchaseDateTo = filter.purchaseDateTo;
        if (typeof filter?.includeArchived === 'boolean') {
          query.includeArchived = filter.includeArchived;
        }
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/admin/procurement',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        if (data) {
          setItems([...data.items]);
          setTotal(data.total);
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { items, total, loading, error, fetchProcurements };
}

export function useAdminProcurementDetail() {
  const [item, setItem] = useState<AdminProcurementDto | null>(null);
  const [events, setEvents] = useState<AdminProcurementEventDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminProcurementApiError | null>(null);

  const fetchProcurement = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/admin/procurement/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setItem(data);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async (id: string) => {
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/admin/procurement/{id}/events',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setEvents([...data.items]);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    }
  }, []);

  return { item, events, loading, error, fetchProcurement, fetchEvents };
}

interface ApiCall<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

export function useAdminProcurementMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminProcurementApiError | null>(null);

  const wrap = useCallback(
    async <T>(fn: () => Promise<ApiCall<T>>): Promise<T | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await fn();
        if (apiErr) {
          setError(toApiError(apiErr, response?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createProcurement = useCallback(
    (body: CreateAdminProcurementDto) =>
      wrap<AdminProcurementDto>(() =>
        apiClient.POST('/api/v1/admin/procurement', { body }) as Promise<
          ApiCall<AdminProcurementDto>
        >,
      ),
    [wrap],
  );

  const updateProcurement = useCallback(
    (id: string, body: UpdateAdminProcurementDto) =>
      wrap<AdminProcurementDto>(() =>
        apiClient.PATCH('/api/v1/admin/procurement/{id}', {
          params: { path: { id } },
          body,
        }) as Promise<ApiCall<AdminProcurementDto>>,
      ),
    [wrap],
  );

  const confirmProcurement = useCallback(
    (id: string, body: ProcurementLifecycleDto) =>
      wrap<AdminProcurementDto>(() =>
        apiClient.POST('/api/v1/admin/procurement/{id}/confirm', {
          params: { path: { id } },
          body,
        }) as Promise<ApiCall<AdminProcurementDto>>,
      ),
    [wrap],
  );

  const voidProcurement = useCallback(
    (id: string, body: ProcurementLifecycleDto) =>
      wrap<AdminProcurementDto>(() =>
        apiClient.POST('/api/v1/admin/procurement/{id}/void', {
          params: { path: { id } },
          body,
        }) as Promise<ApiCall<AdminProcurementDto>>,
      ),
    [wrap],
  );

  const archiveProcurement = useCallback(
    (id: string, body: ProcurementLifecycleDto) =>
      wrap<AdminProcurementDto>(() =>
        apiClient.POST('/api/v1/admin/procurement/{id}/archive', {
          params: { path: { id } },
          body,
        }) as Promise<ApiCall<AdminProcurementDto>>,
      ),
    [wrap],
  );

  const requestPayment = useCallback(
    (id: string, body: RequestPaymentApplicationDto) =>
      wrap<AdminProcurementDto>(() =>
        apiClient.POST('/api/v1/admin/procurement/{id}/payment-request', {
          params: { path: { id } },
          body,
        }) as Promise<ApiCall<AdminProcurementDto>>,
      ),
    [wrap],
  );

  return {
    loading,
    error,
    createProcurement,
    updateProcurement,
    confirmProcurement,
    voidProcurement,
    archiveProcurement,
    requestPayment,
  };
}

export interface ListAdminItemMovementFilter {
  itemName?: string;
  category?: AdminItemMovementDto['category'];
  movementType?: AdminItemMovementDto['movementType'];
  relatedPurchaseId?: string;
  status?: AdminItemMovementDto['status'];
  occurredFrom?: string;
  occurredTo?: string;
  page?: number;
  limit?: number;
}

export function useAdminItemMovements() {
  const [items, setItems] = useState<AdminItemMovementDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminProcurementApiError | null>(null);

  const fetchMovements = useCallback(
    async (filter?: ListAdminItemMovementFilter) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, unknown> = {};
        if (filter?.itemName) query.itemName = filter.itemName;
        if (filter?.category) query.category = filter.category;
        if (filter?.movementType) query.movementType = filter.movementType;
        if (filter?.relatedPurchaseId)
          query.relatedPurchaseId = filter.relatedPurchaseId;
        if (filter?.status) query.status = filter.status;
        if (filter?.occurredFrom) query.occurredFrom = filter.occurredFrom;
        if (filter?.occurredTo) query.occurredTo = filter.occurredTo;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/admin/procurement/movements/list',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        if (data) {
          setItems([...data.items]);
          setTotal(data.total);
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { items, total, loading, error, fetchMovements };
}

export function useAdminItemMovementMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminProcurementApiError | null>(null);

  const recordMovement = useCallback(
    async (body: CreateAdminItemMovementDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/admin/procurement/movements',
          { body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const voidMovement = useCallback(
    async (id: string, body: VoidAdminItemMovementDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/admin/procurement/movements/{id}/void',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, recordMovement, voidMovement };
}

export function useAdminProcurementDashboard() {
  const [summary, setSummary] = useState<AdminProcurementDashboardDto | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminProcurementApiError | null>(null);

  const fetchSummary = useCallback(
    async (month?: string) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, unknown> = {};
        if (month) query.month = month;
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/admin/procurement/dashboard/monthly',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        if (data) setSummary(data);
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { summary, loading, error, fetchSummary };
}
